import React, { useState, useEffect, useRef } from 'react';
import { Switch, message, Space, Form, Spin } from 'antd';
import { DeleteOutlined, CopyOutlined, PlusOutlined, FileExcelOutlined, ImportOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axiosInstance from '../../../variables/api/axiosInstance';
import ConducteurModal from './AddContrat/ConducteurModal';
import EnginForm from './AddContrat/EnginForm';
import GarantiesForm from './AddContrat/GarantiesForm';
import { LOGGED_USER } from '../../../variables/constants';
import { rightsService } from '../../../services/rightsService';
import SouscripteurForm from './AddContrat/SouscripteurForm';
import SideBar from '../../../components/SideBar';
import NavBar from '../../../components/NavBar';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { buildEnginPayload, computeImmatriculation, getEmptyConducteurForm, getEmptyEnginForm, mapApiPersonToSouscripteurForm, mapEnginToUpdateForm } from './AddContrat/data/donnees';
import { dedupeParametresByLibelle, isSameYear } from '../../../variables/functions';
import OldSouscripteurForm from './AddContrat/OldSouscripteurForm';
import FlotteExcelImport from './AddContrat/FlotteExcelImport';
import ExcelImportModal from './AddContrat/ExcelImportModal';
import { useResume } from '../../../components/services/ResumeProvider';

const isTpvCategory = (cat, usage, categories = []) => {
    const code = (typeof cat === "string" ? cat : cat?.codeCategorie) || "";
    const resolvedName =
        (typeof cat === "string"
            ? (categories.find((c) => c.codeCategorie === cat || c.nom === cat)?.nom || "")
            : cat?.nom) || "";
    const upperName = resolvedName.toUpperCase();
    const upperCode = code.toUpperCase();
    const upperUsage = (usage || "").toUpperCase();
    return (
        upperCode.includes("TPV") ||
        upperName.includes("TPV") ||
        upperName.includes("TRANSPORT") ||
        upperUsage === "TPV" ||
        upperUsage.includes("TRANSPORT")
    );
};

// Détermine si l'utilisateur connecté est une compagnie (et non le POOL).
const isCompagnieUser = LOGGED_USER?.profil?.organisme?.typeOrganisme !== "POOL";

// Catégories autorisées pour une compagnie : uniquement MOTO et TPV
// (Transport Public de Voyageurs : taxis, bus/autocars, autocar...).
// Sont explicitement exclus GUM (GUM-MOTO), VT (véhicules de tourisme), etc.
const isCategorieCompagnie = (cat) => {
    const code = (cat?.codeCategorie || "").toUpperCase();
    const nom = (cat?.nom || "").toUpperCase();
    if (code.includes("GUM") || nom.includes("GUM")) return false; // exclure GUM-MOTO
    // TPV : on réutilise le helper existant qui reconnaît aussi les noms
    // "Transport Public de Voyageurs" (qui ne contiennent pas le sigle "TPV").
    if (isTpvCategory(cat)) return true;
    // MOTO (les codes catégorie MOTO commencent par "M", cf. donnees.js)
    return code.startsWith("M") || nom.includes("MOTO");
};

// Filtre la liste des catégories selon le type d'organisme connecté.
const filtrerCategoriesParOrganisme = (cats = []) =>
    isCompagnieUser ? cats.filter(isCategorieCompagnie) : cats;

const NewAddContrat = () => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState(1);
    const [loading, setLoading] = useState(false);
    // Loader du bouton « Suivant » (validation des engins + calcul de l'aperçu /new-preview).
    const [nextLoading, setNextLoading] = useState(false);
    // Garde de ré-entrance synchrone : empêche la double soumission (clics répétés)
    // qui crée des contrats de flotte en doublon. Le `disabled={loading}` du bouton ne
    // suffit pas car `loading` n'est posé qu'après des validations asynchrones.
    const submittingRef = useRef(false);
    const [oldInsured, setOldInsured] = useState(false);
    const [showConducteurModal, setShowConducteurModal] = useState(false);
    const [currentVehicleIndex, setCurrentVehicleIndex] = useState(0);
    const [exonereDeTaxe, setExonereDeTaxe] = useState(false);
    const [categories, setCategories] = useState([]);
    const [marques, setMarques] = useState([]);
    const [professions, setProfessions] = useState([]);
    const [nonCompulsoryGuaranties, setNonCompulsoryGuaranties] = useState([]);
    const [selectedCategorie, setSelectedCategorie] = useState("");
    const [categorie, setCategorie] = useState("");
    const [carosseries, setCarosseries] = useState([]);
    const [genres, setGenres] = useState([]);
    // Listes GLOBALES (repli) conservées à part : permettent, quand une catégorie n'a
    // aucun genre/carrosserie propre, de revenir au global — et NON de garder la liste
    // de la catégorie précédemment sélectionnée (sinon « le genre ne se met pas à jour »).
    const [globalCarosseries, setGlobalCarosseries] = useState([]);
    const [globalGenres, setGlobalGenres] = useState([]);
    const [fractionnements, setFractionnements] = useState([]);
    const [preview, setPreview] = useState("");
    const [accessoiresEdites, setAccessoiresEdites] = useState(null); // override frais accessoires
    const canEditAccessoires = rightsService("HCO079");
    // Bonus/Malus transféré d'une autre compagnie (affaire nouvelle) — SIGNÉ : + = bonus, - = malus
    const [bonusMalusEdite, setBonusMalusEdite] = useState(null);
    // L'édition du taux de bonus/malus est soumise à habilitation (HCO080)
    const canEditBonus = rightsService("HCO080");
    // Contrat « membre du personnel » : bonus pris au max personnel par défaut (droit HCO090).
    const [membrePersonnel, setMembrePersonnel] = useState(false);
    const canMarkPersonnel = rightsService("HCO090");
    // Souscription pour l'État (droit HCO106) : impression sans encaissement, paiement État sous 6 mois.
    const [contratEtat, setContratEtat] = useState(false);
    const [dateEffet, setDateEffet] = useState(dayjs());
    const [fractionnementSelect, setFractionnementSelect] = useState("");
    const [selectedFractionnement, setSelectedFractionnement] = useState("");
    const [loadingEdition, setLoadingEdition] = useState(false);
    const [sameYear, setSameYear] = useState(true);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [oldClient, setOldClient] = useState("");
    const [cSelected, setCSelected] = useState(false);
    const [clientSearch, setClientSearch] = useState("");
    const [clients, setClients] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [tauxCommission, setTauxCommission] = useState(0);
    const [selectedIntermediaire, setSelectedIntermediaire] = useState(null);
    const [showExcelImport, setShowExcelImport] = useState(false);
    const [showFlotteImport, setShowFlotteImport] = useState(false);
    const [agencyProduction, setAgencyProduction] = useState("");

    const navigate = useNavigate();
    const location = useLocation();
    const { pushState, consumeForPage } = useResume();
    const restoredResume = consumeForPage("new-add-contrat");

    // Données du formulaire (état local synchronisé avec Antd Form)
    const [formData, setFormData] = useState([getEmptyEnginForm()]);

    const [personneData, setPersonneData] = useState(getEmptyConducteurForm());

    const param = useParams();

    // initialisation du Form avec valeurs locales
    useEffect(() => {
        axiosInstance.get(`/api/categorie`)
            .then((res) => {
                const visibles = filtrerCategoriesParOrganisme(res.data || []);
                if (!param?.contratId) {
                    setSelectedCategorie(visibles[0]?.codeCategorie);
                    setCategorie(visibles[0]);
                }
                setCategories(res.data);
            }).catch(err => {
                console.error(err);
            });

        axiosInstance.get(`/api/parametre/type/PROFESSION`)
            .then((res) => {
                setProfessions(res.data);
            }).catch(err => {
                console.error(err);
            });

        // Carrosseries globales (repli) : mêmes précautions que pour le genre ci-dessous
        // (dédup par libellé + ne pas écraser une liste filtrée déjà arrivée).
        axiosInstance
            .get(`/api/parametre/type/CAROSSERIE`)
            .then((res) => {
                const list = dedupeParametresByLibelle(res.data);
                setGlobalCarosseries(list);
                setCarosseries((prev) => (prev.length ? prev : list));
            })
            .catch((err) => {
                console.log(err);
            });

        // Genres globaux (repli) : garantit que la liste n'est jamais vide même si
        // l'endpoint filtré par catégorie ne renvoie rien (ou n'est pas encore déployé).
        // Dédupliqué par libellé (une même valeur peut exister pour plusieurs catégories)
        // et ne remplace JAMAIS une liste filtrée déjà arrivée (anti-course).
        axiosInstance
            .get(`/api/parametre/type/GENRE`)
            .then((res) => {
                const list = dedupeParametresByLibelle(res.data);
                setGlobalGenres(list);
                setGenres((prev) => (prev.length ? prev : list));
            })
            .catch((err) => {
                console.log(err);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!restoredResume?.data) return;
        const data = restoredResume.data;
        setActiveTab(data.activeTab ?? 1);
        const restoredEngins = (data.formData || [getEmptyEnginForm()]).map((fd) => ({
            immatriculationFacultative: false,
            ...fd,
        }));
        setFormData(restoredEngins);
        setPersonneData(data.personneData || getEmptyConducteurForm());
        setSelectedCategorie(data.selectedCategorie || "");
        setCategorie(data.categorie || "");
        setFractionnementSelect(data.fractionnementSelect || "");
        setSelectedFractionnement(data.selectedFractionnement || "");
        setExonereDeTaxe(!!data.exonereDeTaxe);
        setDateEffet(data.dateEffet ? dayjs(data.dateEffet) : dayjs());
        setOldInsured(!!data.oldInsured);
        setTauxCommission(data.tauxCommission || 0);
        form.setFieldsValue({
            engins: restoredEngins,
            souscripteur: data.personneData || getEmptyConducteurForm(),
            dateEffet: data.dateEffet ? dayjs(data.dateEffet) : dayjs(),
        });
    }, [restoredResume, form]);

    useEffect(() => {
        form.setFieldsValue({
            engins: formData,
            souscripteur: personneData,
            dateEffet,
        });
    }, [formData, personneData, dateEffet, form]);

    useEffect(() => {
        axiosInstance
            .get(`/api/personne/clients`, {
                params: {
                    page: 0,
                    size: 10,
                    search: clientSearch,
                },
            })
            .then((res) => {
                setClients(res.data?.content?.map((clt) => ({
                    label: clt.prenom ? `${clt.nom} ${clt.prenom}` : `${clt.nom}`,
                    value: JSON.stringify(clt),
                    key: clt.codeId,
                })) || []);
            })
            .catch((err) => {
                console.log(err);
            });
    }, [clientSearch]);

    useEffect(() => {
        axiosInstance.get(`/api/vehicule-marques`)
            .then((res) => {
                setMarques((res.data || []).filter((m) => m.nom));
            }).catch(err => {
                console.error(err);
            });

        selectedCategorie && axiosInstance
            .get(`/api/fractionnement/categorie/code/${selectedCategorie}`)
            .then((res) => {
                // console.log("Fractionnements: ", res.data);
                // if (selectedFractionnement || fractionnementSelect) {
                //     let fs = res.data?.find(f => f.fractionnementId === parseInt(fractionnementSelect)) || res.data?.find(f => f.nombre === parseInt(selectedFractionnement));
                //     setFractionnementSelect(fs ? fs?.fractionnementId : "");
                //     console.log("fractionnementSelect: ", fs);
                // }
                console.log("Fractionnements: ", res.data);
                setFractionnements(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
        // Carrosserie & genre paramétrables PAR CATÉGORIE (+ valeurs globales).
        // On n'écrase la liste globale (chargée au montage) QUE si la requête filtrée
        // par catégorie renvoie effectivement des valeurs ; sinon on conserve le repli
        // global pour ne jamais présenter un Select vide.
        // Valeur par défaut : si la catégorie n'offre qu'UNE seule option (ex. VOITURE
        // pour les catégories véhicule), on pré-remplit les champs encore vides —
        // jamais les valeurs déjà saisies ou chargées (modification/brouillon).
        const applyDefaultIfSingle = (field, list) => {
            const def = list.length === 1 ? list[0]?.libelleParametre : null;
            if (!def) return;
            setFormData((prev) => prev.map((fd) => (fd[field] ? fd : { ...fd, [field]: def })));
        };
        if (selectedCategorie) {
            axiosInstance
                .get(`/api/parametre/type/CAROSSERIE/categorie/${selectedCategorie}`)
                .then((res) => {
                    // Liste PROPRE à la catégorie si elle en a, sinon repli GLOBAL — jamais
                    // la liste de la catégorie précédente (mise à jour systématique).
                    const list = (Array.isArray(res.data) && res.data.length) ? res.data : globalCarosseries;
                    if (list.length) {
                        setCarosseries(list);
                        applyDefaultIfSingle('carosserie', list);
                    }
                })
                .catch((err) => console.log(err));

            axiosInstance
                .get(`/api/parametre/type/GENRE/categorie/${selectedCategorie}`)
                .then((res) => {
                    const list = (Array.isArray(res.data) && res.data.length) ? res.data : globalGenres;
                    if (list.length) {
                        setGenres(list);
                        applyDefaultIfSingle('genre', list);
                    }
                })
                .catch((err) => console.log(err));
        }
        console.log("selectedCategorie: ", selectedCategorie);
        selectedCategorie && someEffects(selectedCategorie);
        // Préremplissage NON destructif : on ne touche qu'aux champs vides/à zéro,
        // jamais aux valeurs déjà saisies/chargées (modification ou brouillon restauré).
        // En modification on n'appelle même pas la fonction. Le changement explicite de
        // catégorie (handleEnginChange) réinitialise, lui, au minimum (onlyEmpty=false).
        selectedCategorie && categories && !param?.contratId && handleValeurVenale(selectedCategorie, categories, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategorie]);

    useEffect(() => {
        const effetIso =
            dateEffet && typeof dateEffet?.toDate === "function"
                ? dateEffet.toDate().toISOString()
                : null;

        // On allège le payload pour rester sous la barre de 50k
        const slimFormData = (formData || []).map((fd) => ({
            categorie: fd.categorie,
            marque: fd.marque,
            numeroSerie: fd.numeroSerie,
            matricule: fd.matricule,
            typeSerie: fd.typeSerie,
            immatriculation: fd.immatriculation,
            immatriculationFacultative: fd.immatriculationFacultative,
            puissance: fd.puissance,
            nombrePlace: fd.nombrePlace,
            energie: fd.energie,
            numeroChassis: fd.numeroChassis,
            valeurDeclaree: fd.valeurDeclaree,
            valeurVenale: fd.valeurVenale,
            valeurANeuf: fd.valeurANeuf,
            dateMiseEnCirculation: fd.dateMiseEnCirculation,
            usage: fd.usage,
            carosserie: fd.carosserie,
            genre: fd.genre,
            rc: fd.rc,
            useSouscripteur: fd.useSouscripteur,
            personne: {
                nom: fd.personne?.nom,
                prenom: fd.personne?.prenom,
                telephone: fd.personne?.telephone,
                genre: fd.personne?.genre,
            },
        }));

        const slimPersonne = {
            nom: personneData?.nom,
            prenom: personneData?.prenom,
            genre: personneData?.genre,
            telephone: personneData?.telephone,
            adresse: personneData?.adresse,
            email: personneData?.email,
        };

        pushState({
            path: `${location.pathname}${location.search}`,
            module: "CONTRATS",
            summary: "Contrat (nouvelle saisie) en cours",
            payload: {
                page: "new-add-contrat",
                data: {
                    activeTab,
                    formData: slimFormData,
                    personneData: slimPersonne,
                    selectedCategorie,
                    categorie,
                    fractionnementSelect,
                    selectedFractionnement,
                    dateEffet: effetIso,
                    exonereDeTaxe,
                    oldInsured,
                    tauxCommission,
                },
            },
        });
    }, [
        activeTab,
        formData,
        personneData,
        selectedCategorie,
        categorie,
        fractionnementSelect,
        selectedFractionnement,
        dateEffet,
        exonereDeTaxe,
        oldInsured,
        tauxCommission,
        location,
        pushState,
    ]);

    useEffect(() => {
        if (param?.contratId) {
            setEditMode(true);
            setLoadingEdition(true);
            axiosInstance
                .get(`/api/contrat/codeId/${param?.contratId}`)
                .then((res) => {
                    const contratFromApi = res.data;
                    setAgencyProduction(contratFromApi.courtier ? contratFromApi?.courtier : contratFromApi?.producteur?.agence);
                    console.log("contratFromApi: ", contratFromApi);
                    let garan = [];
                    someEffects(contratFromApi?.engins[0]?.categorie?.codeCategorie);
                    const mappedFormData = contratFromApi.engins.map((engin, index) => {
                        // Extract the assured person (from engin or fallback to contrat.assure)
                        const assured = engin.assure || contratFromApi.assure;

                        let gar = [];
                        if (engin.sousGaranties) {
                            engin.sousGaranties.forEach((sg) => {
                                gar.push(sg.garantie?.codeId);
                            });
                        }
                        //Remove doublons
                        garan.push(gar);

                        const isy = isSameYear(engin.dateMiseEnCirculation);
                        if (!isy) {
                            setSameYear(false);
                        }

                        return mapEnginToUpdateForm(engin, assured, index);
                    });
                    setLoadingEdition(false);

                    axiosInstance
                        .get(
                            `/api/garantie/categorie/non-compulsory/code/${contratFromApi?.engins[0]?.categorie?.codeCategorie}/${contratFromApi?.organisme?.code}`
                        )
                        .then((res) => {
                            setNonCompulsoryGuaranties(res.data);
                        })
                        .catch((err) => {
                            console.log(err);
                        });
                    axiosInstance
                        .get(`/api/vehicule-marques`)
                        .then((res) => {
                            setMarques((res.data || []).filter((m) => m.nom));
                        })
                        .catch((err) => {
                            console.log(err);
                        });
                    // setGaranties(garan);

                    const persData = mapApiPersonToSouscripteurForm(contratFromApi.souscripteur);
                    setExonereDeTaxe(contratFromApi.souscripteur?.exonereDeTaxe || false);
                    setPersonneData(persData);
                    let engin = contratFromApi?.engins[0];
                    engin.categorie && setSelectedCategorie(engin.categorie?.codeCategorie);
                    engin.categorie && form.setFieldValue(['engins', 0, 'categorie'], engin?.categorie?.codeCategorie);
                    engin.categorie && setCategorie(engin.categorie);

                    let fs = fractionnements?.find(f => f.fractionnementId === parseInt(contratFromApi?.fractionnement?.fractionnementId)) || fractionnements?.find(f => f.nombre === parseInt(contratFromApi?.fractionnement?.fractionnementId));
                    setFractionnementSelect(fs ? fs?.fractionnementId : "");

                    // Précharge les valeurs ÉDITÉES du contrat existant (sinon elles repartent à null
                    // et l'aperçu/la mise à jour les perd).
                    setAccessoiresEdites(contratFromApi?.accessoiresEdites != null ? contratFromApi.accessoiresEdites : null);
                    setBonusMalusEdite(contratFromApi?.bonusMalusEdite != null ? contratFromApi.bonusMalusEdite : null);
                    setMembrePersonnel(!!contratFromApi?.membrePersonnel);
                    setContratEtat(!!contratFromApi?.contratEtat);

                    form.setFieldValue("engins", mappedFormData);
                    setFormData(mappedFormData);
                    // setFractionnementSelect(contratFromApi?.fractionnement?.fractionnementId || null);
                    setSelectedFractionnement(contratFromApi?.fractionnement?.nombre || null);

                    // Aperçu initial = valeurs FIGÉES en base (prime nette, accessoires,
                    // bonus, taxes, total) via previewContrat/previewAN — et non le
                    // recalcul tarifaire (qui diverge de la CP, ex. après reclassement de
                    // catégorie). L'utilisateur peut toujours « Recalculer l'aperçu »
                    // pour une re-tarification en direct après modification.
                    axiosInstance
                        .get(`/api/contrat/preview/${param?.contratId}`)
                        .then((pv) => setPreview(pv.data))
                        .catch((err) => console.log(err));
                }).catch((err) => {
                    console.log(err);
                });

        }
    }, [param?.contratId, categories, fractionnements]);

    // synchroniser valeurs lorsque Form change.
    // ⚠ Les valeurs du Form AntD ne contiennent QUE les champs liés à un Form.Item :
    // `sousGaranties` (garanties supp sélectionnées), `personne`, `conducteurHabituel`, etc.
    // sont gérés HORS du Form. On FUSIONNE donc avec l'état précédent au lieu de l'écraser,
    // sinon toute modif d'un champ (ex. l'année après une erreur de validation) effacerait
    // la garantie supplémentaire déjà cochée.
    const onFormValuesChange = (changedValues, allValues) => {
        // Ne reconstruire le tableau d'engins QUE si la modification concerne les engins.
        // Sinon (ex. saisie du souscripteur), on évitait un remap coûteux de TOUTE la flotte
        // (+ setFormData) à chaque frappe → saisie lente avec beaucoup d'engins.
        if (!changedValues || !('engins' in changedValues)) return;
        if (!allValues.engins) return;
        setFormData((prev) => allValues.engins.map((eng, i) => {
            const previous = prev[i] || {};
            return {
                ...previous,   // conserve les champs hors-Form (sousGaranties, personne, garanties, sg, carteGrise…)
                ...eng,        // applique les valeurs saisies dans le Form
                // garde-fou explicite : ne jamais écraser une sélection existante par un vide/undefined
                sousGaranties: (eng?.sousGaranties && eng.sousGaranties.length)
                    ? eng.sousGaranties
                    : (previous.sousGaranties || []),
            };
        }));
    };

    const someEffects = (value) => {
        axiosInstance
            .get(
                `/api/garantie/categorie/non-compulsory/code/${value}/${LOGGED_USER?.profil?.organisme?.code}`
            )
            .then((res) => {
                setNonCompulsoryGuaranties(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    }

    // Gestion des changements de véhicule non-direct via Form (reste pour actions add/remove)
    const handleVehicleChangeManual = (index, field, value) => {
        let newFormData = [...formData];
        newFormData[index][field] = value;

        if (field === 'categorie') {
            // Changement explicite de catégorie : genre et carrosserie repartent à vide
            // sur tous les engins — les valeurs de l'ancienne catégorie ne sont plus
            // valables (listes filtrées par catégorie). Le défaut de la nouvelle
            // catégorie est pré-rempli au retour des listes (cf. useEffect [selectedCategorie]).
            newFormData = newFormData.map((fd) => ({ ...fd, genre: null, carosserie: null }));

            // Auto-remplissage des valeurs vénale / déclarée / à neuf depuis la valeur
            // vénale minimale de la catégorie choisie. Intégré ICI (au lieu d'un appel à
            // handleValeurVenale) pour que le setFieldsValue final ci-dessous NE l'écrase
            // PAS — choix explicite de catégorie ⇒ on (ré)initialise ces montants.
            const catObj = categories.find((c) => c.codeCategorie === value);
            const venaleMin = catObj?.valeurVenaleMinimale || 0;
            newFormData = newFormData.map((fd) => ({
                ...fd,
                valeurVenale: venaleMin,
                valeurDeclaree: venaleMin,
                valeurANeuf: venaleMin,
            }));

            if (value !== "M") {
                newFormData[index].usage = "TAXI";
                if (newFormData.length > 1) {
                    const newFd = [];
                    newFd[0] = newFormData[0];
                    newFormData = newFd;
                }
            }
            setCategorie(catObj);
            setSelectedCategorie(value);
            someEffects(value);
        }

        // Calcul automatique de l'immatriculation
        if (['numeroSerie', 'matricule', 'typeSerie', 'categorie'].includes(field)) {
            const catValue = newFormData[index]?.categorie || selectedCategorie;
            newFormData[index].immatriculation = computeImmatriculation(
                catValue,
                newFormData[index].typeSerie,
                newFormData[index].numeroSerie,
                newFormData[index].matricule
            );
        }

        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
    };

    const toggleImmatriculationFacultative = (index, checked) => {
        setFormData((prev) => {
            const next = [...prev];
            if (!next[index]) return prev;
            next[index] = {
                ...next[index],
                immatriculationFacultative: checked,
            };
            return next;
        });
        const engins = form.getFieldValue('engins') || [];
        if (engins[index]) {
            engins[index] = { ...engins[index], immatriculationFacultative: checked };
            form.setFieldsValue({ engins });
        }
    };

    // Ajouter un véhicule
    const addVehicle = () => {
        if (!categorie?.codeCategorie?.startsWith("M")) {
            message.warning('Vous ne pouvez ajouter plusieurs véhicules que pour la catégorie MOTO');
            return;
        }

        const newVehicle = {
            ...formData[0],
            numeroSerie: "",
            matricule: "",
            immatriculation: "",
            numeroChassis: "",
            personne: getEmptyConducteurForm(),
            useSouscripteur: true,
        };
        const newFormData = [...formData, newVehicle];
        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        message.success('Nouveau véhicule ajouté');
    };

    // Copier les données du premier véhicule
    const copyFirstVehicleData = () => {
        if (formData.length < 2) return;

        const newFormData = [...formData];
        const lastIndex = formData.length - 1;
        const firstVehicle = formData[0];

        newFormData[lastIndex] = {
            ...firstVehicle,
            numeroSerie: "",
            matricule: "",
            immatriculation: "",
            numeroChassis: "",
            personne: firstVehicle.useSouscripteur
                ? getEmptyConducteurForm()
                : { ...firstVehicle.personne, codeId: "", contact: [] },
            useSouscripteur: firstVehicle.useSouscripteur,
        };

        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        message.success('Données copiées avec succès');
    };

    // Supprimer un véhicule
    const removeVehicle = (index) => {
        if (formData.length === 1) {
            message.warning('Vous devez avoir au moins un véhicule');
            return;
        }
        const newFormData = formData.filter((_, i) => i !== index);
        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        message.success('Véhicule supprimé');
    };

    // Ouvrir modal conducteur
    const openConducteurModal = (index) => {
        setCurrentVehicleIndex(index);
        setShowConducteurModal(true);
    };

    // Enregistrer conducteur depuis la modal (objet conducteur validé)
    const handleConducteurSave = (conducteur) => {
        const newFormData = [...formData];
        newFormData[currentVehicleIndex].personne = {
            ...conducteur,
            contact: conducteur.telephone ? [conducteur.telephone] : (conducteur.contact || []),
        };
        // si conducteur renseigné => useSouscripteur false
        const hasConducteurInfo = conducteur.nom || conducteur.prenom || conducteur.adresse || conducteur.telephone;
        newFormData[currentVehicleIndex].useSouscripteur = !hasConducteurInfo;

        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        setShowConducteurModal(false);
        message.success('Conducteur enregistré');
    };

    // Validation du formulaire via Antd Form pour engins
    const validateVehicleForm = async (forceImmatriculation = false) => {
        const engs = form.getFieldValue('engins') || formData;

        // Garde-fou explicite : refuser les DOUBLONS d'immatriculation / châssis au sein de
        // la même flotte. Le validateur asynchrone par champ peut être contourné (champ non
        // monté, import Excel…) ; ce contrôle synchrone bloque le passage à l'étape suivante.
        const seenImmat = new Map();
        const seenChassis = new Map();
        for (let i = 0; i < engs.length; i++) {
            const e = engs[i] || {};
            const immat = (e.immatriculation || '').trim().toLowerCase();
            const chassis = (e.numeroChassis || '').trim().toLowerCase();
            if (immat) {
                if (seenImmat.has(immat)) {
                    message.error(`Immatriculation en doublon dans la flotte : « ${e.immatriculation} » (engins N°${seenImmat.get(immat) + 1} et N°${i + 1}).`);
                    return false;
                }
                seenImmat.set(immat, i);
            }
            if (chassis) {
                if (seenChassis.has(chassis)) {
                    message.error(`N° de châssis en doublon dans la flotte : « ${e.numeroChassis} » (engins N°${seenChassis.get(chassis) + 1} et N°${i + 1}).`);
                    return false;
                }
                seenChassis.set(chassis, i);
            }
        }

        // Garde-fou valeurs : interdire une valeur vénale / déclarée / à neuf nulle ou ≤ 0.
        // La règle `required` du Form laisse passer 0 (un nombre n'est pas « vide »), ce qui
        // permettait d'enregistrer des engins à 0 — données ensuite irrécupérables en édition.
        for (let i = 0; i < engs.length; i++) {
            const e = engs[i];
            const vv = Number(e?.valeurVenale);
            const vd = Number(e?.valeurDeclaree);
            const vn = Number(e?.valeurANeuf);
            if (!(vv > 0) || !(vd > 0) || !(vn > 0)) {
                message.error(`Engin N°${i + 1} : les valeurs vénale, déclarée et à neuf doivent être supérieures à 0.`);
                return false;
            }
        }

        const names = [];

        // construire la liste des chemins de champs à valider pour chaque engin
        engs.forEach((e, i) => {
            names.push(['engins', i, 'categorie']);
            const validateImmat = forceImmatriculation || !isTpvCategory(e?.categorie || selectedCategorie, e?.usage) || !e?.immatriculationFacultative;
            if (validateImmat) {
                names.push(['engins', i, 'typeSerie']);
                names.push(['engins', i, 'matricule']);
                // si typeSerie TG alors numeroSerie obligatoire
                if ((e && e.typeSerie) === 'TG') {
                    names.push(['engins', i, 'numeroSerie']);
                }
            }
            names.push(['engins', i, 'marque']);
            names.push(['engins', i, 'energie']);
            names.push(['engins', i, 'usage']);
            names.push(['engins', i, 'volume']);
            names.push(['engins', i, 'nombrePlace']);
            names.push(['engins', i, 'genre']);
            names.push(['engins', i, 'moteur']);
            names.push(['engins', i, 'type']);
            names.push(['engins', i, 'numeroChassis']);
            names.push(['engins', i, 'valeurVenale']);
            names.push(['engins', i, 'valeurDeclaree']);
            names.push(['engins', i, 'valeurANeuf']);
            names.push(['engins', i, 'dateMiseEnCirculation']);
            // carrosserie requise uniquement si catégorie MOTO
            const categorie = e?.categorie ?? (engs[0]?.categorie);
            if (categorie?.codeCategorie?.startsWith("M")) {
                names.push(['engins', i, 'carosserie']);
            }

            // Check if same year in order to setup fractionnements
            if (e?.dateMiseEnCirculation) {
                console.log('Check dateMiseEnCirculation: ', e?.dateMiseEnCirculation?.toISOString());
                const isy = isSameYear(e?.dateMiseEnCirculation?.toISOString());
                console.log('Same year: ', isy);
                if (!isy) {
                    setSameYear(false);
                }
            }
        });

        try {
            await form.validateFields(names);
            if (forceImmatriculation) {
                const missingImmat = engs.some((e) =>
                    !e?.typeSerie ||
                    !e?.matricule ||
                    ((e?.typeSerie === 'TG') && !e?.numeroSerie)
                );
                if (missingImmat) {
                    message.error("Veuillez renseigner l'immatriculation pour valider le contrat.");
                    return false;
                }
            }
            return true;
        } catch (err) {
            // affiche les erreurs sur les Form.Item (rouge) — scroll vers la première erreur si possible
            const firstError = err?.errorFields && err.errorFields[0];
            if (firstError && firstError.name) {
                try { form.scrollToField(firstError.name); } catch (e) { /* ignore */ }
            }
            return false;
        }
    };

    // onlyEmpty=true : ne préremplit QUE les champs vides/à zéro (préremplissage
    // non destructif, utilisé au montage / restauration de brouillon).
    // onlyEmpty=false (défaut) : réinitialise au minimum (choix explicite de catégorie).
    const handleValeurVenale = (categorieCode, cats, onlyEmpty = false) => {
        const categorie = cats.find(cat => cat.codeCategorie === categorieCode);
        if (categorie) {
            const min = categorie.valeurVenaleMinimale || 0;
            const isBlank = (v) => v === undefined || v === null || v === "" || Number(v) === 0;
            const newFormData = [...formData];
            newFormData.forEach((e, i) => {
                if (!onlyEmpty || isBlank(newFormData[i].valeurVenale)) newFormData[i].valeurVenale = min;
                if (!onlyEmpty || isBlank(newFormData[i].valeurDeclaree)) newFormData[i].valeurDeclaree = min;
                if (!onlyEmpty || isBlank(newFormData[i].valeurANeuf)) newFormData[i].valeurANeuf = min;
            });
            setFormData(newFormData);
            form.setFieldsValue({ engins: newFormData });
        }
    };

    // Validation du souscripteur via Antd Form
    const validateSouscripteur = async (sous) => {
        try {
            const phonePattern = /^(22|90|91|92|93|96|98|99|70|71|79|97)\d{6}$/;
            console.log('Souscripteur', sous);
            if (!sous?.contact || !sous.contact[0] || !phonePattern.test(sous.contact[0])) {
                console.log('Numéro de téléphone du souscripteur invalide:', sous?.contact?.[0]);
                message.error('Numéro de téléphone du souscripteur invalide');
                return false;
            } else if (!sous?.nom && sous.typePersonne === 'MORALE') {
                console.log('Veuillez remplir tous les champs obligatoires du souscripteur');
                message.error('Veuillez remplir tous les champs obligatoires du souscripteur');
                return false;
            } else if ((!sous?.nom || !sous?.prenom) && sous.typePersonne === 'PHYSIQUE') {
                console.log('Veuillez remplir tous les champs obligatoires du souscripteur');
                message.error('Veuillez remplir tous les champs obligatoires du souscripteur');
                return false;
            } else if (!sous?.adresse) {
                console.log('Adresse du souscripteur manquante');
                message.error('Adresse du souscripteur manquante');
                return false;
            } else if (!sous?.genre) {
                console.log('Genre du souscripteur manquant');
                message.error('Genre du souscripteur manquant');
                return false;
            }
            return true;
        } catch (err) {
            message.error('Veuillez remplir tous les champs obligatoires du souscripteur');
            return false;
        }
    };

    const handleSubmit = async () => {
        // Garde synchrone contre la double soumission : si un enregistrement est déjà
        // en cours, on ignore les clics suivants (évite les contrats de flotte en doublon).
        if (submittingRef.current) return;
        submittingRef.current = true;

        const vehOk = await validateVehicleForm();
        if (!vehOk) { submittingRef.current = false; return; }

        if (!oldInsured) {
            const sousOk = await validateSouscripteur(personneData);
            if (!sousOk) { submittingRef.current = false; return; }
        }


        setLoading(true);
        const engins = (form.getFieldValue('engins') || formData).map(e => ({
            ...e,
            dateMiseEnCirculation: e?.dateMiseEnCirculation ? e.dateMiseEnCirculation.toISOString() : null,
        }));
        const fract = fractionnementSelect ? fractionnements.find((e) => e.fractionnementId === parseInt(fractionnementSelect)) : null;

        const payload = {
            engins: engins.map((fd) => buildEnginPayload(fd, selectedCategorie, categories)),
            souscripteur: personneData,
            dateEffet: dateEffet?.toISOString() || dayjs().toISOString(),
            fractionnement: fract,
            tauxCommission: tauxCommission,
            intermediaire: selectedIntermediaire || null,
            organisme: JSON.parse(sessionStorage.getItem('currentCompagnie')) || LOGGED_USER?.profil?.organisme,
            courtier: agencyProduction ? agencyProduction : null,
            accessoiresEdites: (canEditAccessoires && accessoiresEdites != null && accessoiresEdites !== "")
                ? Number(accessoiresEdites) : null,
            bonusMalusEdite: ((canEditBonus || membrePersonnel) && bonusMalusEdite != null && bonusMalusEdite !== "")
                ? Number(bonusMalusEdite) : null,
            membrePersonnel: !!membrePersonnel,
            contratEtat: !!contratEtat,
        };

        console.log('Données à soumettre:', payload);
        let url = `/api/contrat/new`;
        if (oldInsured) {
            payload.oldSouscripteur = oldClient;
            url = `/api/contrat/new/old-client`;
        }
        if (param?.contratId) {
            payload.codeId = param.contratId;
            axiosInstance
                .put(url, payload)
                .then((res) => {
                    console.log('Contrat mis à jour:', res.data);
                    setLoading(false);
                    submittingRef.current = false;
                    navigate(`/details-contrat/${res.data.codeId}`, { replace: true });
                    message.success('Contrat mis à jour avec succès!');
                })
                .catch((err) => {
                    console.log('Erreur lors de la mise à jour du contrat:', err);
                    message.error('Une erreur est survenue lors de la mise à jour du contrat');
                    setLoading(false);
                    submittingRef.current = false;
                });
        } else {
            axiosInstance
                .post(url, payload)
                .then((res) => {
                    console.log('Contrat créé:', res.data);
                    setLoading(false);
                    submittingRef.current = false;
                    navigate(`/details-contrat/${res.data.codeId}`, { replace: true });
                    message.success('Contrat créé avec succès!');
                })
                .catch((err) => {
                    console.log('Erreur lors de la création du contrat:', err);
                    message.error('Une erreur est survenue lors de la création du contrat');
                    setLoading(false);
                    submittingRef.current = false;
                });
        }
    };

    const showPreview = async () => {
        try {
            setLoadingPreview(true);
            const fract = fractionnementSelect ? fractionnements.find((e) => e.fractionnementId === parseInt(fractionnementSelect)) : null;
            const engins = formData.map((fd) => buildEnginPayload(fd, selectedCategorie, categories));
            const contratData = {
                engins: engins,
                dateEffet: dateEffet?.toISOString() || dayjs().toISOString(),
                fractionnement: fract,
                accessoiresEdites: (canEditAccessoires && accessoiresEdites != null && accessoiresEdites !== "")
                    ? Number(accessoiresEdites) : null,
                bonusMalusEdite: ((canEditBonus || membrePersonnel) && bonusMalusEdite != null && bonusMalusEdite !== "")
                    ? Number(bonusMalusEdite) : null,
                membrePersonnel: !!membrePersonnel,
                contratEtat: !!contratEtat,
            }

            console.log("DataToPREVIEW: ", contratData);
            const response = await axiosInstance.post(`/api/contrat/new-preview`, contratData);
            console.log("PREVIEW: ", response.data);
            setPreview(response.data);
            setLoadingPreview(false);
        } catch (err) {
            console.log(err);
            toast("Une erreur est survenue");
        }
    }

    useEffect(() => {
        if (activeTab === 2)
            showPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fractionnementSelect, dateEffet]);

    // NB : l'aperçu n'est PLUS recalculé automatiquement à la saisie des frais accessoires
    // ou du bonus/malus. La réactualisation se fait via la touche Entrée ou le bouton
    // « Recalculer l'aperçu » (cf. GarantiesForm) → showPreview().

    // Fonction qui gere l'importation
    const handleExcelImportSuccess = (importedData) => {
        if (importedData.length === 0) return;

        // Prendre le premier contrat importé
        const firstContract = importedData[0];

        // Mettre à jour le souscripteur
        setPersonneData(firstContract.souscripteur);
        setExonereDeTaxe(firstContract.souscripteur.exonereDeTaxe);

        // Mettre à jour l'engin
        const enginData = [{
            ...getEmptyEnginForm(),
            ...firstContract.engin
        }];

        setFormData(enginData);
        form.setFieldsValue({
            engins: enginData,
            souscripteur: firstContract.souscripteur,
            dateEffet: firstContract.dateEffet
        });

        // Mettre à jour les autres champs
        setDateEffet(firstContract.dateEffet);
        setTauxCommission(firstContract.tauxCommission);

        // Trouver la catégorie
        const cat = categories.find(c => c.codeCategorie === firstContract.engin.categorie);
        if (cat) {
            setSelectedCategorie(cat.codeCategorie);
            setCategorie(cat);
        }

        message.success('Données importées avec succès!');
    };

    // Normalise un libellé de garantie pour l'appariement : sans accents, espaces réduits, casse.
    const normGaranti = (s) => String(s || '')
        .normalize('NFKD').replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ').trim().toLowerCase();

    // Apparie les libellés de garanties du fichier Excel (« VOL », « DEFENSE RECOURS »…) aux
    // garanties RÉELLES en base et renvoie leurs codeId (= la valeur attendue par le Select).
    // Les libellés non reconnus sont ignorés (pas de sélection fantôme).
    const matchGarantiesToCodeIds = (names) => {
        if (!Array.isArray(names)) return [];
        const list = nonCompulsoryGuaranties || [];
        return names
            .map((n) => {
                const target = normGaranti(n);
                const found = list.find((g) => normGaranti(g.nom) === target);
                return found ? found.codeId : null;
            })
            .filter(Boolean);
    };

    // Résout les SOUS-GARANTIES (avec primes) d'un jeu de garanties — comme le fait
    // handleEnginChange lors d'une sélection manuelle. Sans cela, le preview ne « voit » pas
    // les garanties importées tant qu'on ne retire/rajoute pas une garantie. Cache par jeu.
    const resolveSousGarantiesByCodeIds = async (codeIds, cache) => {
        const ids = Array.isArray(codeIds) ? codeIds : [];
        const key = [...ids].sort().join('|');
        if (!key) return [];
        if (cache[key]) return cache[key];
        const gtys = ids
            .map((id) => (nonCompulsoryGuaranties || []).find((g) => g.codeId === id))
            .filter(Boolean);
        try {
            const res = await axiosInstance.post(`/api/sous-garantie/garanties`, JSON.stringify(gtys));
            cache[key] = res.data || [];
        } catch (e) {
            console.log(e);
            cache[key] = [];
        }
        return cache[key];
    };

    const handleFlotteImportSuccess = async (importedEngins) => {
        if (importedEngins.length === 0) return;

        // 1) Apparier les garanties aux codeId réels.
        let droppedGaranties = 0;
        const withCodeIds = importedEngins.map((engin) => {
            const rawGaranties = Array.isArray(engin.garantiesSupplementaires) ? engin.garantiesSupplementaires : [];
            const matched = matchGarantiesToCodeIds(rawGaranties);
            droppedGaranties += Math.max(0, rawGaranties.length - matched.length);
            return {
                ...engin,
                garantiesSupplementaires: matched,
                categorie: formData[0]?.categorie || selectedCategorie, // Garder la catégorie actuelle
            };
        });
        if (droppedGaranties > 0) {
            message.warning(`${droppedGaranties} garantie(s) supplémentaire(s) du fichier non reconnue(s) et ignorée(s).`);
        }

        // 2) Résoudre les sous-garanties (primes) pour que le preview les prenne en compte
        //    immédiatement, sans manipulation manuelle. Cache par jeu de garanties (une flotte
        //    a en général le même jeu pour tous les engins → un seul appel API).
        const hide = message.loading('Préparation des garanties de la flotte…', 0);
        const cache = {};
        const newFormData = [];
        for (const engin of withCodeIds) {
            const sousGaranties = await resolveSousGarantiesByCodeIds(engin.garantiesSupplementaires, cache);
            // `imported: true` → les validateurs châssis/immatriculation NE REFONT PAS la vérif
            // en base (coûteuse : 1 appel API + 1 setFieldsValue par engin → cascade de
            // re-renders qui fait ramer/crasher la page sur une grosse flotte).
            newFormData.push({ ...engin, sousGaranties, imported: true });
        }
        hide();

        // 3) Mettre à jour le state (une seule fois, avec garanties + sous-garanties prêtes).
        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });

        message.success(`${importedEngins.length} engin(s) importé(s) avec succès dans la flotte!`);

        // Vérifier si toutes les dates sont de la même année
        const allSameYear = importedEngins.every(e => {
            if (e.dateMiseEnCirculation) {
                return isSameYear(e.dateMiseEnCirculation.toISOString());
            }
            return true;
        });
        setSameYear(allSameYear);
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PRODUCTION"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={{
                                engins: formData,
                                souscripteur: personneData,
                                dateEffet: dayjs(),
                            }}
                            onValuesChange={onFormValuesChange}
                        >
                            <div className="container-xxl flex-grow-1 container-p-y">
                                <div className="row">
                                    <div className="col-12 mb-4" style={{ background: '#E6F0FF' }}>
                                        <small className="text-light fw-medium">{param?.contratId ? "Modifier le contrat" : "Ajouter un contrat"}</small>

                                        <div className="bs-stepper vertical wizard-modern wizard-modern-vertical wizard-modern-vertical-icons-example mt-2">
                                            {/* STEPPER HEADER */}
                                            <div className="bs-stepper-header">
                                                <div className={activeTab === 1 ? "step active" : "step"}>
                                                    <button type="button" className="step-trigger" onClick={() => setActiveTab(1)}>
                                                        <span className="bs-stepper-circle">
                                                            <i className="bx bx-car"></i>
                                                        </span>
                                                        <span className="bs-stepper-label">
                                                            <span className="bs-stepper-title">Informations sur l'engin</span>
                                                            <span className="bs-stepper-subtitle">{param?.contratId ? "Modifier" : "Ajouter"} les infos sur l'engin</span>
                                                        </span>
                                                    </button>
                                                </div>

                                                <div className="line"></div>

                                                <div className={activeTab === 2 ? "step active" : "step"}>
                                                    <button
                                                        type="button"
                                                        className="step-trigger"
                                                        onClick={async (e) => {
                                                            e.preventDefault();
                                                            const ok = await validateVehicleForm();
                                                            ok && setActiveTab(2);
                                                        }}
                                                    >
                                                        <span className="bs-stepper-circle">
                                                            <i className="bx bx-detail"></i>
                                                        </span>
                                                        <span className="bs-stepper-label">
                                                            <span className="bs-stepper-title">Détails des garanties</span>
                                                            <span className="bs-stepper-subtitle">Configurer les détails des garanties</span>
                                                        </span>
                                                    </button>
                                                </div>

                                                <div className="line"></div>

                                                <div className={activeTab === 3 ? "step active" : "step"}>
                                                    <button
                                                        type="button"
                                                        className="step-trigger"
                                                        onClick={async (e) => {
                                                            e.preventDefault();
                                                            const ok = await validateVehicleForm();
                                                            ok && setActiveTab(3);
                                                        }}
                                                    >
                                                        <span className="bs-stepper-circle">
                                                            <i className="bx bx-user"></i>
                                                        </span>
                                                        <span className="bs-stepper-label">
                                                            <span className="bs-stepper-title">Infos sur le souscripteur</span>
                                                            <span className="bs-stepper-subtitle">{param?.contratId ? "Modifier" : "Renseigner"} les infos sur le souscripteur</span>
                                                        </span>
                                                    </button>
                                                </div>

                                                <div className="line"></div>

                                                <Space direction="vertical">
                                                    <Switch
                                                        checkedChildren="Assuré existant"
                                                        unCheckedChildren="Nouvel assuré"
                                                        checked={oldInsured}
                                                        onChange={setOldInsured}
                                                    />
                                                </Space>
                                                <div className="line"></div>
                                                <div className="line"></div>
                                                <br />
                                                {sessionStorage.getItem("currentCompagnie") ? <div
                                                    style={{
                                                        border: "2px solid #242166",
                                                        borderRadius: 8,
                                                        padding: 10,
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        transition: "all 0.3s",
                                                        backgroundColor: "#fafafa",
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
                                                >
                                                    Vous êtes en train de produire un contrat pour :
                                                    <br />
                                                    <img
                                                        src={`data:image/png;base64,${JSON.parse(sessionStorage.getItem("currentCompagnie")).logoDisplay}`}
                                                        alt={JSON.parse(sessionStorage.getItem("currentCompagnie")).raisonSociale}
                                                        style={{ width: "150px", height: "150px", objectFit: "contain", marginBottom: 10 }}
                                                    />
                                                    <div style={{ fontWeight: "bold" }}> {JSON.parse(sessionStorage.getItem("currentCompagnie")).raisonSociale}</div>
                                                </div> : null}

                                                <div className="line"></div>
                                                <br />
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => setShowExcelImport(true)}
                                                >
                                                    <ImportOutlined /> Importer des contrats
                                                </button>
                                            </div>
                                            <Spin size="large" spinning={loadingEdition} >
                                                {/* STEPPER CONTENT */}
                                                <div className="bs-stepper-content">
                                                    {/* ÉTAPE 1: VÉHICULES */}
                                                    {activeTab === 1 && (
                                                        <div className="content active">
                                                            <div className="content-header mb-4">
                                                                <h6 className="mb-0">Informations sur l'engin</h6>
                                                                <small>Veuillez renseigner les informations sur l'engin</small>
                                                            </div>

                                                            <div className="row g-6">
                                                                {/* Sélection catégorie */}
                                                                <div className="col-sm-12">
                                                                    <Form.Item
                                                                        label="Catégorie *"
                                                                        name={['engins', 0, 'categorie']}
                                                                        rules={[{ required: true, message: 'La catégorie est requise' }]}
                                                                    >
                                                                        <select
                                                                            className="form-control"
                                                                            onChange={(e) => handleVehicleChangeManual(0, 'categorie', e.target.value)}
                                                                        >
                                                                            {filtrerCategoriesParOrganisme(categories)?.map((cat, idx) => (
                                                                                <option key={idx} value={cat.codeCategorie}>{cat.nom}</option>
                                                                            ))}
                                                                        </select>
                                                                    </Form.Item>
                                                                </div>
                                                                {isTpvCategory(selectedCategorie || categorie, formData[0]?.usage, categories) && (
                                                                    <div className="col-sm-12">
                                                                        <Space size={8} align="center">
                                                                            <span className="text-muted" style={{ fontSize: "12px" }}>
                                                                                Est-ce un véhicule à l'immatriculation ?
                                                                            </span>
                                                                            <Switch
                                                                                size="small"
                                                                                checked={!!formData[0]?.immatriculationFacultative}
                                                                                onChange={(checked) => toggleImmatriculationFacultative(0, checked)}
                                                                            />
                                                                        </Space>
                                                                    </div>
                                                                )}

                                                                {/* Liste des véhicules */}
                                                                {formData.length === 1 ? (
                                                                    <EnginForm
                                                                        index={0}
                                                                        marques={marques}
                                                                        carosseries={carosseries}
                                                                        genres={genres}
                                                                        form={form}
                                                                        setSameYear={setSameYear}
                                                                        removeVehicle={removeVehicle}
                                                                        openConducteurModal={openConducteurModal}
                                                                        garantiesNonObligatoires={nonCompulsoryGuaranties || []}
                                                                        categorie={categorie}
                                                                        onEnginsUpdate={(engs) => {
                                                                            setFormData((prev) => {
                                                                                const merged = (engs || []).map((eng, i) => {
                                                                                    const previous = prev[i] || {};
                                                                                    return {
                                                                                        ...previous,
                                                                                        ...eng,
                                                                                        // `sousGaranties` n'est pas un champ du Form : on l'écrase
                                                                                        // uniquement quand l'update le porte explicitement (changement
                                                                                        // de garantie), sinon on conserve la sélection précédente.
                                                                                        sousGaranties: Array.isArray(eng?.sousGaranties)
                                                                                            ? eng.sousGaranties
                                                                                            : (previous.sousGaranties || []),
                                                                                    };
                                                                                });
                                                                                form.setFieldsValue({ engins: merged });
                                                                                return merged;
                                                                            });
                                                                        }}
                                                                        editMode={editMode}
                                                                    />
                                                                ) : (
                                                                    <div className="accordion mt-4" id="accordionVehicles">
                                                                        {formData.map((vehicle, index) => (
                                                                            <div className="card accordion-item" key={index}>
                                                                                <h2 className="accordion-header" id={`heading${index}`}>
                                                                                    <button
                                                                                        className="accordion-button"
                                                                                        type="button"
                                                                                        data-bs-toggle="collapse"
                                                                                        data-bs-target={`#accordion${index}`}
                                                                                    >
                                                                                        Engin N° {index + 1}
                                                                                        {!vehicle.useSouscripteur && (
                                                                                            <span className="badge bg-info ms-2">
                                                                                                Conducteur: {vehicle?.personne?.nom} {vehicle?.personne?.prenom}
                                                                                            </span>
                                                                                        )}
                                                                                    </button>
                                                                                </h2>
                                                                                <div
                                                                                    id={`accordion${index}`}
                                                                                    className="accordion-collapse collapse show"
                                                                                    data-bs-parent="#accordionVehicles"
                                                                                >
                                                                                    <div className="accordion-body">
                                                                                        <EnginForm
                                                                                            index={index}
                                                                                            marques={marques}
                                                                                            carosseries={carosseries}
                                                                                            genres={genres}
                                                                                            form={form}
                                                                                            setSameYear={setSameYear}
                                                                                            removeVehicle={removeVehicle}
                                                                                            openConducteurModal={openConducteurModal}
                                                                                            garantiesNonObligatoires={nonCompulsoryGuaranties || []}
                                                                                            categorie={categorie}
                                                                                            onEnginsUpdate={(engs) => {
                                                                                                setFormData((prev) => {
                                                                                                    const merged = (engs || []).map((eng, i) => {
                                                                                                        const previous = prev[i] || {};
                                                                                                        return {
                                                                                                            ...previous,
                                                                                                            ...eng,
                                                                                                            sousGaranties: Array.isArray(eng?.sousGaranties)
                                                                                                                ? eng.sousGaranties
                                                                                                                : (previous.sousGaranties || []),
                                                                                                        };
                                                                                                    });
                                                                                                    form.setFieldsValue({ engins: merged });
                                                                                                    return merged;
                                                                                                });
                                                                                            }}
                                                                                            editMode={editMode}
                                                                                        />

                                                                                        {index > 0 && (
                                                                                            <div className="mt-3">
                                                                                                <button
                                                                                                    className="btn btn-danger"
                                                                                                    onClick={() => removeVehicle(index)}
                                                                                                >
                                                                                                    <DeleteOutlined /> Supprimer
                                                                                                </button>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {/* Boutons d'action pour véhicules */}
                                                                {categorie?.codeCategorie?.startsWith("M") && (
                                                                    <div className="col-12">
                                                                        <div className="d-flex gap-2">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-primary"
                                                                                onClick={addVehicle}
                                                                            >
                                                                                <PlusOutlined /> Ajouter un engin
                                                                            </button>

                                                                            {formData.length > 1 && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-warning"
                                                                                    onClick={copyFirstVehicleData}
                                                                                >
                                                                                    <CopyOutlined /> Copier les données
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-success"
                                                                                onClick={() => setShowFlotteImport(true)}
                                                                            >
                                                                                <FileExcelOutlined /> Importer une flotte depuis Excel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Navigation */}
                                                                <div className="col-12 d-flex justify-content-between">
                                                                    <button type="button" className="btn btn-label-secondary" disabled>
                                                                        <i className="bx bx-left-arrow-alt me-2"></i>
                                                                        Précédent
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        disabled={nextLoading}
                                                                        onClick={async () => {
                                                                            if (nextLoading) return;
                                                                            setNextLoading(true);
                                                                            try {
                                                                                const isValid = await validateVehicleForm();
                                                                                if (isValid) {
                                                                                    setActiveTab(2);
                                                                                    // En édition, on garde l'aperçu FIGÉ (base/CP) chargé
                                                                                    // au montage ; le recalcul live reste dispo via le
                                                                                    // bouton « Recalculer l'aperçu ».
                                                                                    if (!editMode) await showPreview();
                                                                                }
                                                                            } finally {
                                                                                setNextLoading(false);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {nextLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                                                                        {nextLoading ? 'Calcul en cours...' : 'Suivant'}
                                                                        {!nextLoading && <i className="bx bx-right-arrow-alt ms-2"></i>}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <FlotteExcelImport
                                                                visible={showFlotteImport}
                                                                onClose={() => setShowFlotteImport(false)}
                                                                onImportSuccess={handleFlotteImportSuccess}
                                                                marques={marques}
                                                                carosseries={carosseries}
                                                                categorie={selectedCategorie}
                                                                valeurParDefaut={250000}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* ÉTAPE 2: GARANTIES */}
                                                    {activeTab === 2 && (
                                                        <div className="content active">
                                                            <div className="content-header mb-4">
                                                                <h6 className="mb-0">Détails de garanties</h6>
                                                                <small>Veuillez entrer les détails de garanties</small>
                                                            </div>
                                                            <div className="row g-6">
                                                                <GarantiesForm
                                                                    categorie={selectedCategorie}
                                                                    selectedCategorie={selectedCategorie}
                                                                    fractionnements={fractionnements || []}
                                                                    preview={preview}
                                                                    dateEffet={dateEffet}
                                                                    setDateEffet={setDateEffet}
                                                                    fractionnementSelect={fractionnementSelect}
                                                                    setFractionnementSelect={setFractionnementSelect}
                                                                    showPreview={showPreview}
                                                                    sameYear={sameYear}
                                                                    loadingPreview={loadingPreview}
                                                                    canEditAccessoires={canEditAccessoires}
                                                                    accessoiresEdites={accessoiresEdites}
                                                                    setAccessoiresEdites={setAccessoiresEdites}
                                                                    bonusMalusEdite={bonusMalusEdite}
                                                                    setBonusMalusEdite={setBonusMalusEdite}
                                                                    membrePersonnel={membrePersonnel}
                                                                    setMembrePersonnel={setMembrePersonnel}
                                                                    canMarkPersonnel={canMarkPersonnel}
                                                                    contratEtat={contratEtat}
                                                                    setContratEtat={setContratEtat}
                                                                    tauxCommission={tauxCommission}
                                                                    setTauxCommission={setTauxCommission}
                                                                    agencyProduction={agencyProduction}
                                                                    setAgencyProduction={setAgencyProduction}
                                                                    setIntermediaire={setSelectedIntermediaire}
                                                                />
                                                                <div className="col-12 d-flex justify-content-between">
                                                                    <button type="button" className="btn btn-label-secondary" onClick={() => setActiveTab(1)}>
                                                                        <i className="bx bx-left-arrow-alt me-2"></i>
                                                                        Précédent
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        onClick={async () => setActiveTab(3)}
                                                                    >
                                                                        Suivant
                                                                        <i className="bx bx-right-arrow-alt ms-2"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* ÉTAPE 3: SOUSCRIPTEUR */}
                                                    {activeTab === 3 && (
                                                        <div className="content active">
                                                            <div className="content-header mb-4">
                                                                <h6 className="mb-0">Informations du souscripteur</h6>
                                                                <small>Renseigner les informations du souscripteur</small>
                                                            </div>

                                                            <div className="row g-6">
                                                                {/* Type de personne */}
                                                                <div className="col-12">
                                                                    <div className="card">
                                                                        <div className="card-body">
                                                                            {oldInsured ?
                                                                                <OldSouscripteurForm oldClient={oldClient} setOldClient={setOldClient} clients={clients} clientSearch={clientSearch} setClientSearch={setClientSearch} cSelected={cSelected} setCSelected={setCSelected} loading={loading} /> :
                                                                                <SouscripteurForm professions={professions} formData={personneData} setFormData={setPersonneData} />
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-sm-6">
                                                                    <Form.Item name="exonereDeTaxe" valuePropName="checked">
                                                                        <Switch
                                                                            checkedChildren="OUI"
                                                                            unCheckedChildren="NON"
                                                                            checked={exonereDeTaxe}
                                                                            onChange={setExonereDeTaxe}
                                                                        />
                                                                    </Form.Item>
                                                                    <span className="ms-2">Exonéré de taxe?</span>
                                                                </div>

                                                                <div className="col-12 d-flex justify-content-between">
                                                                    <button className="btn btn-primary" onClick={() => setActiveTab(2)}>
                                                                        <i className="bx bx-left-arrow-alt me-2"></i>
                                                                        Précédent
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-success"
                                                                        onClick={handleSubmit}
                                                                        disabled={loading}
                                                                    >
                                                                        {loading && <span className="spinner-border spinner-border-sm me-2"></span>}
                                                                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <ExcelImportModal
                                                        visible={showExcelImport}
                                                        onClose={() => setShowExcelImport(false)}
                                                        onImportSuccess={handleExcelImportSuccess}
                                                        categories={categories}
                                                        professions={professions}
                                                    />
                                                </div>

                                            </Spin>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            {/* Modal Conducteur */}
                            <ConducteurModal
                                visible={showConducteurModal}
                                onClose={() => setShowConducteurModal(false)}
                                conducteur={formData[currentVehicleIndex]?.personne || getEmptyConducteurForm()}
                                onSave={handleConducteurSave}
                                professions={professions}
                                enginIndex={currentVehicleIndex}
                            />
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default NewAddContrat;
